<script>
  import { onMount } from 'svelte';
  import Modal from '../components/Modal.svelte';
  import CountrySelect from '../components/CountrySelect.svelte';
  import TimezoneSelect from '../components/TimeZoneSelect.svelte';
  import { apiFetch, user, logout } from '../lib/auth.js';
  import { createEventDispatcher } from 'svelte';
  import { customReviewsEnabled, setCustomReviewsEnabled } from '../lib/customReviewsCookie.js';

  export let open = false;

  const dispatch = createEventDispatcher();

  let profile    = null;
  let loading    = true;
  let error      = '';

  // Edit state
  let editing      = false;
  let editBio      = '';
  let editCountry  = '';
  let editTimezone = '';
  let editPicUrl   = '';
  let saveLoading  = false;
  let saveError    = '';
  let saveSuccess  = false;

  // Change password state
  let changingPassword  = false;
  let currentPassword   = '';
  let newPassword       = '';
  let confirmPassword   = '';
  let pwLoading         = false;
  let pwError           = '';
  let pwSuccess         = false;

  $: if (open) loadProfile();
  $: if (!open) reset();

  async function loadProfile() {
    loading = true; error = '';
    try {
      profile = await apiFetch('/users/me');
    } catch (e) {
      error = e.message || 'Failed to load profile';
    } finally {
      loading = false;
    }
  }

  function startEdit() {
    editBio      = profile?.bio      || '';
    editCountry  = profile?.country  || '';
    editTimezone = profile?.timezone || 'UTC';
    editPicUrl   = profile?.profile_picture_url || '';
    saveError    = '';
    saveSuccess  = false;
    editing      = true;
  }

  function cancelEdit() {
    editing   = false;
    saveError = '';
  }

  async function saveChanges() {
    saveError   = '';
    saveLoading = true;
    try {
      const body = {
        bio:                 editBio.trim()     || null,
        country:             editCountry.trim() || null,
        timezone:            editTimezone.trim() || 'UTC',
        profile_picture_url: editPicUrl.trim()  || null,
      };
      profile = await apiFetch('/users/me', { method: 'PUT', body: JSON.stringify(body) });
      saveSuccess = true;
      setTimeout(() => { editing = false; saveSuccess = false; }, 1000);
    } catch (e) {
      saveError = e.message || 'Failed to save';
    } finally {
      saveLoading = false;
    }
  }

  function startChangePassword() {
    currentPassword  = '';
    newPassword      = '';
    confirmPassword  = '';
    pwError          = '';
    pwSuccess        = false;
    changingPassword = true;
  }

  function cancelChangePassword() {
    changingPassword = false;
    pwError          = '';
  }

  async function submitChangePassword() {
    pwError = '';

    if (newPassword.length < 8) {
      pwError = 'New password must be at least 8 characters';
      return;
    }
    if (newPassword !== confirmPassword) {
      pwError = 'Passwords do not match';
      return;
    }

    pwLoading = true;
    try {
      await apiFetch('/users/me/password', {
        method: 'PUT',
        body: JSON.stringify({
          current_password: currentPassword,
          new_password:     newPassword,
        }),
      });
      pwSuccess = true;
      setTimeout(() => { changingPassword = false; pwSuccess = false; }, 1500);
    } catch (e) {
      pwError = e.message || 'Failed to update password';
    } finally {
      pwLoading = false;
    }
  }

  function handleLogout() {
    dispatch('close');
    logout();
  }

  function reset() {
    editing          = false;
    saveError        = '';
    saveSuccess      = false;
    changingPassword = false;
    pwError          = '';
    pwSuccess        = false;
  }

  function formatDate(str) {
    if (!str) return '—';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  const STATUS_COLORS = {
    active:      '#6db800',
    pending:     '#cc8833',
    banned:      '#cc3300',
    deactivated: '#444',
  };

  function toggleCustomReviews() {
    setCustomReviewsEnabled(!$customReviewsEnabled);
  }
</script>

<Modal title="Account" {open} on:close={() => dispatch('close')}>
  <div class="account">

    {#if loading}
      <div class="state"><span class="spinner"></span> Loading...</div>

    {:else if error}
      <div class="state error">{error}</div>

    {:else if profile}

      {#if !editing && !changingPassword}
        <!-- ── Profile view ── -->
        <div class="profile">
          <div class="avatar">
            {#if profile.profile_picture_url}
              <img src={profile.profile_picture_url} alt={profile.username} />
            {:else}
              <span>{(profile.username || '?')[0].toUpperCase()}</span>
            {/if}
          </div>
          <div class="profile-info">
            <span class="username">{profile.username}</span>
            <span class="email">{profile.email}</span>
            <div class="meta-row">
              <span class="joined">Since {formatDate(profile.created_at)}</span>
              {#if profile.country}
                <span class="meta-pill">{profile.country}</span>
              {/if}
              <span class="status-pill" style="color: {STATUS_COLORS[profile.status] || '#888'}">
                {profile.status || 'unknown'}
              </span>
            </div>
          </div>
        </div>

        {#if profile.bio}
          <div class="bio-block">{profile.bio}</div>
        {/if}

        <div class="detail-row">
          <span class="detail-label">Timezone</span>
          <span class="detail-value">{profile.timezone || 'UTC'}</span>
        </div>

        <!-- ── Custom Reviews toggle ── -->
        <div class="detail-row feature-row">
          <div class="feature-info">
            <span class="detail-label">Custom Reviews</span>
            <span class="feature-hint">Cigars, whisky &amp; more</span>
          </div>
          <button
            class="toggle"
            class:on={$customReviewsEnabled}
            on:click={toggleCustomReviews}
            aria-pressed={$customReviewsEnabled}
            title={$customReviewsEnabled ? 'Disable custom reviews' : 'Enable custom reviews'}
          >
            <span class="toggle-track">
              <span class="toggle-thumb"></span>
            </span>
            <span class="toggle-label">{$customReviewsEnabled ? 'ON' : 'OFF'}</span>
          </button>
        </div>

        <div class="actions">
          <button class="btn-edit" on:click={startEdit}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit Profile
          </button>
          <button class="btn-logout" on:click={handleLogout}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Log Out
          </button>
        </div>

        <button class="btn-change-pw" on:click={startChangePassword}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          Change Password
        </button>

      {:else if editing}
        <!-- ── Edit form ── -->
        {#if saveSuccess}
          <div class="save-success">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Saved
          </div>

        {:else}
          <div class="section-title">Edit Profile</div>

          {#if saveError}
            <div class="form-error">{saveError}</div>
          {/if}

          <div class="field">
            <label for="edit-bio">Bio</label>
            <textarea id="edit-bio" bind:value={editBio} placeholder="Tell us about yourself…" rows="3"></textarea>
          </div>

          <div class="row">
            <div class="field">
              <label>Country</label>
              <CountrySelect bind:value={editCountry} />
            </div>
            <div class="field">
              <label>Timezone</label>
              <TimezoneSelect bind:value={editTimezone} />
            </div>
          </div>

          <div class="field">
            <label for="edit-pic">Profile Picture URL</label>
            <input id="edit-pic" type="url" bind:value={editPicUrl} placeholder="https://…" />
          </div>

          <div class="btn-row">
            <button class="btn-secondary" on:click={cancelEdit} disabled={saveLoading}>Cancel</button>
            <button class="btn-primary" on:click={saveChanges} disabled={saveLoading}>
              {saveLoading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        {/if}

      {:else if changingPassword}
        <!-- ── Change password form ── -->
        {#if pwSuccess}
          <div class="save-success">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Password Updated
          </div>

        {:else}
          <div class="section-title">Change Password</div>

          {#if pwError}
            <div class="form-error">{pwError}</div>
          {/if}

          <div class="field">
            <label for="current-pw">Current Password</label>
            <input id="current-pw" type="password" bind:value={currentPassword} placeholder="••••••••" autocomplete="current-password" />
          </div>

          <div class="field">
            <label for="new-pw">New Password <span class="hint">min 8 chars</span></label>
            <input id="new-pw" type="password" bind:value={newPassword} placeholder="••••••••" autocomplete="new-password" />
          </div>

          <div class="field">
            <label for="confirm-pw">Confirm New Password</label>
            <input id="confirm-pw" type="password" bind:value={confirmPassword} placeholder="••••••••" autocomplete="new-password" />
          </div>

          <div class="btn-row">
            <button class="btn-secondary" on:click={cancelChangePassword} disabled={pwLoading}>Cancel</button>
            <button class="btn-primary" on:click={submitChangePassword} disabled={pwLoading}>
              {pwLoading ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        {/if}
      {/if}

    {/if}

  </div>
</Modal>

<style>
  .account {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    min-height: 120px;
  }

  /* ── State messages ── */
  .state {
    display: flex; align-items: center; gap: 0.6rem;
    padding: 2rem 0; justify-content: center;
    font-family: 'Rajdhani', sans-serif; font-size: 0.7rem;
    letter-spacing: 0.15em; text-transform: uppercase; color: #2a5200;
  }
  .state.error { color: #cc6633; }

  .spinner {
    width: 14px; height: 14px; flex-shrink: 0;
    border: 2px solid #1e3d00; border-top-color: #6db800;
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Profile view ── */
  .profile {
    display: flex; align-items: center; gap: 1rem;
  }

  .avatar {
    width: 52px; height: 52px; flex-shrink: 0;
    background: #1a3300; border: 1px solid #3a6b00; border-radius: 3px;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 0 12px rgba(74,124,0,0.25); overflow: hidden;
  }
  .avatar img { width: 100%; height: 100%; object-fit: cover; }
  .avatar span {
    font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 1.5rem; color: #6db800; line-height: 1;
    text-shadow: 0 0 8px rgba(109,184,0,0.6);
  }

  .profile-info {
    display: flex; flex-direction: column; gap: 0.2rem; min-width: 0;
  }
  .username {
    font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 1.1rem; color: #e8e8e8; letter-spacing: 0.05em;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .email {
    font-family: 'Rajdhani', sans-serif; font-weight: 600;
    font-size: 0.75rem; color: #4a7c00; letter-spacing: 0.05em;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .meta-row {
    display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap;
  }
  .joined {
    font-family: 'Rajdhani', sans-serif; font-weight: 600;
    font-size: 0.55rem; color: #2a5200; letter-spacing: 0.15em; text-transform: uppercase;
  }
  .meta-pill {
    font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    background: #0d1a00; border: 1px solid #1e3d00; border-radius: 2px;
    padding: 0.1rem 0.35rem; color: #3a6b00;
  }
  .status-pill {
    font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
  }

  /* ── Bio block ── */
  .bio-block {
    background: #080f00; border: 1px solid #1e3d00; border-radius: 2px;
    padding: 0.65rem 0.75rem;
    font-family: 'Rajdhani', sans-serif; font-size: 0.85rem; font-weight: 600;
    color: #888; line-height: 1.5;
  }

  /* ── Detail row ── */
  .detail-row {
    display: flex; align-items: center; gap: 0.75rem;
    border-top: 1px solid #0f1a00; padding-top: 0.75rem;
  }
  .detail-label {
    font-family: 'Rajdhani', sans-serif; font-size: 0.55rem; font-weight: 700;
    letter-spacing: 0.2em; text-transform: uppercase; color: #2a5200; flex-shrink: 0;
  }
  .detail-value {
    font-family: 'Rajdhani', sans-serif; font-size: 0.75rem; font-weight: 600; color: #555;
  }

  /* ── Feature toggle row ── */
  .feature-row {
    justify-content: space-between;
    align-items: center;
  }
  .feature-info {
    display: flex; flex-direction: column; gap: 0.15rem;
  }
  .feature-hint {
    font-family: 'Rajdhani', sans-serif; font-size: 0.55rem; font-weight: 600;
    color: #2a5200; letter-spacing: 0.05em;
  }

  /* ── Toggle switch (horizontal) ── */
  .toggle {
    display: flex; flex-direction: row; align-items: center; gap: 0.5rem;
    background: none; border: none; cursor: pointer; padding: 0;
    flex-shrink: 0;
  }
  .toggle-track {
    position: relative; width: 36px; height: 20px;
    background: #0d1a00; border: 1px solid #1e3d00; border-radius: 10px;
    transition: background 0.25s, border-color 0.25s;
    flex-shrink: 0;
  }
  .toggle.on .toggle-track {
    background: #1a3300; border-color: #3a6b00;
    box-shadow: 0 0 8px rgba(109,184,0,0.3);
  }
  .toggle-thumb {
    position: absolute; top: 2px; left: 2px;
    width: 14px; height: 14px; border-radius: 50%;
    background: #2a5200;
    transition: transform 0.25s, background 0.25s;
  }
  .toggle.on .toggle-thumb {
    transform: translateX(16px);
    background: #6db800;
    box-shadow: 0 0 6px rgba(109,184,0,0.6);
  }
  .toggle-label {
    font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 0.55rem; letter-spacing: 0.2em;
    color: #2a5200; transition: color 0.25s; min-width: 1.8em;
  }
  .toggle.on .toggle-label { color: #6db800; }

  /* ── Action buttons ── */
  .actions { display: flex; gap: 0.75rem; }

  .btn-edit, .btn-logout {
    flex: 1; display: flex; align-items: center; justify-content: center; gap: 0.4rem;
    font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 0.6rem; letter-spacing: 0.2em; text-transform: uppercase;
    padding: 0.6rem 0.75rem; border-radius: 2px; cursor: pointer;
    transition: background 0.2s, border-color 0.2s, color 0.2s;
  }
  .btn-edit svg, .btn-logout svg { width: 14px; height: 14px; flex-shrink: 0; }

  .btn-edit { background: #1a3300; border: 1px solid #3a6b00; color: #6db800; }
  .btn-edit:hover { background: #223d00; box-shadow: 0 0 8px rgba(109,184,0,0.2); }

  .btn-logout { background: transparent; border: 1px solid #2a1000; color: #5a2a00; }
  .btn-logout:hover { border-color: #5a2000; color: #cc4400; background: rgba(204,68,0,0.06); }

  /* ── Change password button ── */
  .btn-change-pw {
    display: flex; align-items: center; justify-content: center; gap: 0.4rem;
    width: 100%; background: transparent; border: 1px solid #1e3d00;
    color: #2a5200; font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 0.6rem; letter-spacing: 0.2em; text-transform: uppercase;
    padding: 0.55rem 0.75rem; border-radius: 2px; cursor: pointer;
    transition: border-color 0.2s, color 0.2s, background 0.2s;
    margin-top: -0.25rem;
  }
  .btn-change-pw svg { width: 13px; height: 13px; flex-shrink: 0; }
  .btn-change-pw:hover { border-color: #3a6b00; color: #4a7c00; background: rgba(74,124,0,0.05); }

  /* ── Section title ── */
  .section-title {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem;
    letter-spacing: 0.3em; text-transform: uppercase; color: #3a6b00;
    border-left: 3px solid #3a6b00; padding-left: 0.6rem;
  }

  /* ── Edit / password forms ── */
  .save-success {
    display: flex; align-items: center; justify-content: center; gap: 0.6rem;
    padding: 2rem; color: #6db800;
    font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 1rem; letter-spacing: 0.4em; text-transform: uppercase;
    text-shadow: 0 0 10px rgba(109,184,0,0.5);
  }
  .save-success svg { width: 22px; height: 22px; stroke: #6db800; }

  .form-error {
    background: rgba(204,68,0,0.1); border: 1px solid rgba(204,68,0,0.3); border-radius: 2px;
    color: #cc6633; font-family: 'Rajdhani', sans-serif; font-size: 0.65rem;
    letter-spacing: 0.1em; padding: 0.6rem 0.75rem; text-transform: uppercase;
  }

  .field { display: flex; flex-direction: column; gap: 0.4rem; }
  .field label {
    font-family: 'Rajdhani', sans-serif; font-weight: 600;
    font-size: 0.55rem; letter-spacing: 0.25em; text-transform: uppercase; color: #3a6b00;
  }
  .hint { font-weight: 400; color: #2a5200; text-transform: none; letter-spacing: 0.05em; }

  .field input, .field textarea {
    background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    color: #c8c8c8; font-family: 'Rajdhani', sans-serif;
    font-size: 1rem; font-weight: 600; padding: 0.6rem 0.75rem;
    width: 100%; outline: none; transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
  }
  .field textarea { resize: vertical; min-height: 80px; }
  .field input:focus, .field textarea:focus { border-color: #3a6b00; box-shadow: 0 0 0 2px rgba(74,124,0,0.15); }

  .row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
  .row :global(.search-field) { margin-bottom: 0; }

  .btn-row { display: flex; gap: 0.75rem; padding-top: 0.25rem; }
  .btn-primary {
    flex: 1; background: #1a3300; border: 1px solid #3a6b00; color: #6db800;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem;
    letter-spacing: 0.25em; text-transform: uppercase; padding: 0.7rem;
    cursor: pointer; border-radius: 2px; transition: background 0.2s, box-shadow 0.2s;
  }
  .btn-primary:hover { background: #223d00; box-shadow: 0 0 10px rgba(109,184,0,0.25); }
  .btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }

  .btn-secondary {
    background: transparent; border: 1px solid #1e3d00; color: #444;
    font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.6rem;
    letter-spacing: 0.2em; text-transform: uppercase; padding: 0.5rem 1rem;
    cursor: pointer; border-radius: 2px; transition: border-color 0.2s, color 0.2s;
  }
  .btn-secondary:hover { border-color: #3a6b00; color: #6db800; }
  .btn-secondary:disabled { opacity: 0.4; cursor: not-allowed; }
</style>
